import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { Reveal } from "./motion";
import { formatDate } from "../lib/api";

export const SectionHeading = ({
  eyebrow,
  title,
  description,
  light = false,
  align = "left",
  className = "",
}) => (
  <Reveal className={`${align === "center" ? "text-center mx-auto max-w-3xl" : "max-w-3xl"} ${className}`}>
    <p className={light ? "eyebrow-on-dark" : "eyebrow"}>{eyebrow}</p>
    <h2
      className={`mt-5 text-3xl font-medium leading-[1.12] tracking-tight sm:text-4xl lg:text-[2.75rem] ${
        light ? "text-white" : "text-ink"
      }`}
    >
      {title}
    </h2>
    {description && (
      <p className={`mt-5 text-base leading-relaxed ${light ? "text-white/60" : "text-steel"}`}>
        {description}
      </p>
    )}
  </Reveal>
);

export const DraftBadge = () => (
  <span
    className="inline-flex items-center gap-1.5 rounded-full border border-gold/50 bg-gold/10 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.18em] text-gold-dark"
    title="Draft content for review"
  >
    Draft
  </span>
);

export const ArticleCard = ({ article, index = 0 }) => (
  <Reveal delay={index * 0.08} className="h-full">
    <Link
      to={`/insights/${article.slug}`}
      data-testid={`article-card-${article.slug}`}
      className="group flex h-full flex-col card-line p-8 transition-all duration-500 hover:border-gold/60 hover:shadow-[0_24px_60px_-30px_rgba(11,21,40,0.35)]"
    >
      <div className="flex items-center justify-between gap-3">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-gold-dark">
          {article.category}
        </span>
        <DraftBadge />
      </div>
      <h3 className="mt-5 font-serif text-2xl leading-snug text-ink transition-colors group-hover:text-gold-dark">
        {article.title}
      </h3>
      <p className="mt-3 line-clamp-3 flex-1 text-sm leading-relaxed text-steel">
        {article.excerpt}
      </p>
      <div className="mt-6 flex items-center justify-between border-t border-line pt-5">
        <span className="font-mono text-[10px] uppercase tracking-[0.16em] text-steel/70">
          {formatDate(article.published_at)} · {article.read_minutes} min read
        </span>
        <ArrowUpRight
          size={17}
          className="text-steel transition-all duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-gold-dark"
        />
      </div>
    </Link>
  </Reveal>
);

export const ServiceRow = ({ service, index = 0 }) => (
  <Reveal delay={index * 0.05} y={20}>
    <Link
      to={`/services/${service.slug}`}
      data-testid={`service-row-${service.slug}`}
      className="group grid grid-cols-[auto_1fr_auto] items-center gap-5 border-t border-navy/15 py-7 transition-all duration-500 sm:gap-8 sm:py-9 last:border-b hover:bg-cream/70"
    >
      <span className="font-mono text-xs text-gold-dark">{service.num}</span>
      <div>
        <h3 className="font-serif text-2xl text-ink transition-all duration-500 group-hover:translate-x-2 group-hover:text-gold-dark sm:text-3xl">
          {service.title}
        </h3>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-steel">
          {service.summary}
        </p>
      </div>
      <span className="flex h-11 w-11 items-center justify-center rounded-full border border-navy/20 transition-all duration-500 group-hover:border-gold group-hover:bg-gold group-hover:text-navy">
        <ArrowRight size={17} className="transition-transform duration-500 group-hover:-rotate-45" />
      </span>
    </Link>
  </Reveal>
);
